// =====================================================================
//  Onglet « Message » d'une stratégie OTP.
//  Choix des canaux (Mail / SMS) + sélection du Template existant porteur
//  du code/lien pour chaque canal actif.
//  Les templates mail proviennent du projet (endpoint réel /template/liste).
//  Les templates SMS sont à venir (liste vide pour l'instant).
// =====================================================================

import {IDisposableLike} from "../../Tools/Disposable/IDisposableLike";
import {
    IOtpStrategyModel,
    OtpChannel,
    OtpChannelMask,
} from "../../Model/Otp/OtpStrategyModel.js";
import {TemplateRead} from "../../Controller/Template/TemplateRead.js";

interface ITemplateOption {
    text: string;
    value: string;
}

export class OtpMessageEditor implements IDisposableLike {
    #jqMainContainer: JQuery | undefined;
    #jqFormContainer: JQuery | undefined;
    #model: IOtpStrategyModel;
    #kForm: kendo.ui.Form | undefined;
    #mailTpl: ITemplateOption[] = [];
    #smsTpl: ITemplateOption[] = [];

    constructor(options: IOtpMessageEditorOptions) {
        this.#jqMainContainer = options.jqMainContainer;
        this.#model = options.model;
        if (this.#model.Channels === undefined) this.#model.Channels = 0;
    }

    public Init = async () => {
        this.#buildHtml();
        await this.#loadTemplates();
        this.#buildForm();
    };

    #buildHtml = () => {
        this.#jqFormContainer = $("<div></div>")
            .css({padding: "12px", maxWidth: "640px"})
            .appendTo(this.#jqMainContainer);
    };

    #loadTemplates = async () => {
        if (!this.#model.FkProjet) {
            this.#mailTpl = [];
        } else {
            const list = await TemplateRead.ReadList({
                AsNoTracking: true,
                FkProjet: this.#model.FkProjet,
            });
            // Tous les templates du projet sont éligibles comme porteur mail.
            this.#mailTpl = (list ?? []).map((t) => ({
                text: t.Nom ?? "(sans nom)",
                value: t.Primarykey ?? "",
            }));
        }
        // Templates SMS : à venir (aucun pour l'instant).
        this.#smsTpl = [];
    };

    #buildForm = () => {
        const mailTpl = this.#mailTpl;
        const smsTpl = this.#smsTpl;
        const formData: any = {
            MailEnabled: OtpChannelMask.Has(this.#model.Channels, OtpChannel.Mail),
            SmsEnabled: OtpChannelMask.Has(this.#model.Channels, OtpChannel.Sms),
            FkMailTemplate: this.#model.FkMailTemplate ?? "",
            FkSmsTemplate: this.#model.FkSmsTemplate ?? "",
        };

        this.#kForm = this.#jqFormContainer
            .kendoForm({
                formData,
                orientation: "vertical",
                size: "small",
                items: [
                    {
                        field: "MailEnabled",
                        label: "Canal e-mail",
                        editor: "Switch",
                    },
                    {
                        field: "FkMailTemplate",
                        label: "Template e-mail",
                        editor: "DropDownList",
                        editorOptions: {
                            optionLabel: "— Choisir un template —",
                            dataTextField: "text",
                            dataValueField: "value",
                            dataSource: mailTpl,
                        },
                    },
                    {
                        field: "SmsEnabled",
                        label: "Canal SMS",
                        editor: "Switch",
                    },
                    {
                        field: "FkSmsTemplate",
                        label: "Template SMS",
                        editor: "DropDownList",
                        editorOptions: {
                            optionLabel: smsTpl.length
                                ? "— Choisir un template —"
                                : "— Aucun template SMS (à venir) —",
                            dataTextField: "text",
                            dataValueField: "value",
                            dataSource: smsTpl,
                            enable: smsTpl.length > 0,
                        },
                    },
                ],
                change: (e: kendo.ui.FormChangeEvent) => {
                    switch (e.field) {
                        case "MailEnabled":
                            this.#toggleChannel(OtpChannel.Mail, e.value);
                            break;
                        case "SmsEnabled":
                            this.#toggleChannel(OtpChannel.Sms, e.value);
                            break;
                        case "FkMailTemplate":
                            this.#model.FkMailTemplate = e.value || undefined;
                            break;
                        case "FkSmsTemplate":
                            this.#model.FkSmsTemplate = e.value || undefined;
                            break;
                    }
                },
            })
            .data("kendoForm");

        this.#kForm.element.find(".k-form-buttons").remove();

        // Note d'aide sous le formulaire.
        $("<div></div>")
            .css({marginTop: "10px", fontSize: "12px", color: "#888"})
            .html(
                "Le code/lien est porté par un <b>template existant</b> du projet. " +
                "Le canal SMS suppose un paramètre d'envoi SMS configuré au niveau du client (appel d'API).",
            )
            .appendTo(this.#jqFormContainer);
    };

    #toggleChannel = (channel: OtpChannel, enabled: boolean) => {
        this.#model.Channels = OtpChannelMask.Set(this.#model.Channels, channel, enabled);
    };

    public Dispose = () => {
        if (this.#kForm) this.#kForm.destroy();
        if (this.#jqMainContainer) this.#jqMainContainer.empty();
    };
}

export interface IOtpMessageEditorOptions {
    jqMainContainer: JQuery;
    model: IOtpStrategyModel;
}
